import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  useMediaQuery,
  alpha,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FaArrowRight,
  FaArrowLeft,
} from 'react-icons/fa';
import { useTheme } from '@mui/material/styles';
import BookMyOpAPI from '../../services/BookMyOpapi';

// Styled Components
const StyledContainer = styled(Box)(({ theme }) => ({
  
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: '0 24px 24px 24px',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(6),
  },
  background: theme.palette.background.default,
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.2rem',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(1.5),
  },
  [theme.breakpoints.up('lg')]: { 
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.up('xl')]: {
    marginBottom: theme.spacing(3),
  },
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(0.5),
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(1.5),
  },
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.up('xl')]: {
    gap: theme.spacing(3),
  },
  overflowX: 'auto',
  overflowY: 'visible',
  scrollBehavior: 'smooth',
  // paddingBottom: theme.spacing(1),
  paddingX: theme.spacing(0.5),
  [theme.breakpoints.up('md')]: {
    paddingX: theme.spacing(1),
  },
  '&::-webkit-scrollbar': {
    height: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.primary.main, 0.1),
    borderRadius: 2,
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.3),
    borderRadius: 2,
  },
  '@media (max-width: 600px)': {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
}));

const StyledSpecialtyCard = styled(Card)(({ theme }) => ({
  width: 50,
  height: 50,
  
  [theme.breakpoints.up('sm')]: {
    width: 60,
    height: 60,
  },
  [theme.breakpoints.up('md')]: {
    width: 70,
    height: 70,
  },
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${theme.palette.primary.main} !important`,
  cursor: 'pointer',
  border: 'none',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.05)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 1)} 0%, ${alpha(theme.palette.secondary.main, 1)} 100%)`,
  },
}));

const StyledSpecialtyText = styled(Box)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.8rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '0.9rem',
  },
  color: "white !important",
  textAlign: 'center',
  whiteSpace: 'normal',
  minHeight: '2.4em',
  [theme.breakpoints.up('sm')]: {
    minHeight: '2.6em',
  },
  width: '100%',
  lineHeight: 1.2,
  cursor: 'pointer',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',

}));

const ViewAllButton = styled(Button)(({ theme }) => ({
 
  borderRadius: '20px',
  // padding: theme.spacing(0.5),
  textTransform: 'none',
  fontSize: '0.8rem',
  [theme.breakpoints.up('md')]: {
    fontSize: '0.8rem',
  },
  fontWeight: 600,
  letterSpacing: '0.05em',
  color: `${theme.palette.primary.main}`,
  background: `white`,
  border: 'none',
  '&:hover': {
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    transform: 'translateY(-2px)',
    color: 'white',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '& .MuiButton-endIcon': {
    color: 'white',
  },
  transition: 'all 0.3s ease',
}));

const ScrollButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  color: theme.palette.primary.main,
  padding: 0,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.2),
    transform: 'scale(1.1)',
  },
  '&:disabled': {
    background: alpha(theme.palette.grey[400], 0.1),
    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
    color: theme.palette.grey[400],
  },
  transition: 'all 0.2s ease',
}));


const Specialties = ({ onSpecialtySelect, onViewAll, selectedSpecialty, noPadding = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef(null);
  
  // State for specialties data
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false); // Prevent duplicate calls in React Strict Mode

  // Fetch specialties from API
  useEffect(() => {
    const fetchSpecialties = async () => {
      // Prevent duplicate calls
      if (hasFetched.current) {
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        hasFetched.current = true;
        
        const response = await BookMyOpAPI.getSpecialties();
        setSpecialties(response.results || response.data || response);
      } catch (err) {
        console.error('Error fetching specialties:', err);
        setError('Failed to load specialties. Please try again.');
        hasFetched.current = false; // Reset on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialties();
  }, []);

  const handleViewAllSpecialists = () => {
    // If parent provides onViewAll callback, use it (for coordinated filtering)
    // Otherwise, navigate directly (backward compatibility)
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/find-doctors');
    }
  };

  const handleSpecialtyClick = (specialtyName) => {
    // If parent provides onSpecialtySelect callback, use it (for coordinated filtering)
    // Otherwise, navigate directly (backward compatibility)
    if (onSpecialtySelect) {
      onSpecialtySelect(specialtyName);
    } else {
      navigate(`/find-doctors?specialty=${encodeURIComponent(specialtyName)}`);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth'
      });
    }
  };

  const SpecialtyCard = ({ specialty, isSelected }) => {
    // Get the specialty name from API data (could be 'name' or 'title' field)
    const specialtyName = specialty.name || specialty.title || specialty;
    
    return (
      <Box
        sx={{
          minWidth: { xs: '85px', sm: '90px', md: '100px', lg: '110px' },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flex: 'none',
          // px: { xs: 0.5, md: 0.75 },
          mb:1
        }}
      >
        <StyledSpecialtyText onClick={() => handleSpecialtyClick(specialtyName)}>
          <Chip 
            sx={{ 
              fontSize:'0.8rem',
              fontWeight: isSelected ? 800 : 700,
              backgroundColor: isSelected 
                ? theme.palette.primary.dark 
                : theme.palette.primary.main,
              color: 'white',
              transform: isSelected ? 'scale(1.02)' : 'scale(1)',
              '& .MuiChip-label': {
                color: 'white !important'
              },
              transition: 'all 0.3s ease',
            }} 
            label={specialtyName} 
          />
        </StyledSpecialtyText>
      </Box>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </StyledContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <StyledContainer>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer sx={noPadding ? { padding: '0 !important' } : {}}>
      <Box>
        {/* <StyledTitle variant="h4" sx={{ml:1,mb:3, 
          textAlign:'center',textTransform:'uppercase',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          // letterSpacing:'0.05em',
          }}>
          Find By Speciality
        </StyledTitle> */}

        <Box sx={{ px: noPadding ? 0 : { xs: 1, md: 3 }, position: 'relative' }}>
          <ScrollContainer 
            ref={scrollContainerRef}
            sx={noPadding ? { paddingX: '0 !important', paddingLeft: '0 !important', paddingRight: '0 !important' } : {}}
          >
            {specialties.map((specialty, index) => (
              <SpecialtyCard
                key={specialty.id || specialty.name || index}
                specialty={specialty}
                isSelected={selectedSpecialty && specialty.id && String(specialty.id) === String(selectedSpecialty)}
              />
            ))}
          </ScrollContainer>
<Box sx={{display:'flex',flexDirection:'row',justifyContent:'space-around',alignItems:'center'}}>
          {/* Left Arrow */}
          <ScrollButton
            onClick={scrollLeft}
            sx={{
              position: 'absolute',
              left: { xs: 2, md: 8 },
              
              bgcolor:"transparent",
              border:'none',
              zIndex: 1,
              display: 'flex'
            }}
          >
            <FaArrowLeft size={10} />
          </ScrollButton>
          {/* View All Specialities button removed as per requirement */}
        {/*
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt:{xs:0,md:2,lg:2,xl:2}

          }}
        >
          <ViewAllButton
            variant="contained"
            size="x-small"

            onClick={handleViewAllSpecialists}
          >
            View All Specialities
          </ViewAllButton>
        </Box>
        */}
          {/* Right Arrow */}
          <ScrollButton
            onClick={scrollRight}
            sx={{
              position: 'absolute',
              right: { xs: 4, md: 8 },
             bgcolor:"transparent",
             border:'none',
            
              zIndex: 1,
              display: 'flex'
            }}
          >
            <FaArrowRight size={10} />
          </ScrollButton>
        </Box>
        </Box>
       
      </Box>
    </StyledContainer>
  );
};

export default Specialties;